import React from 'react';
import ArrowIcon from 'public/icons/custom-icons/arrow';

interface PaginationProps {
  className?: string;
  currentPage: number;
  totalPages: number;
  onPageClick: (page: number) => void;
  onNextPageClick: () => void;
  onPrevPageClick: () => void;
}

const Pagination: React.FC<PaginationProps> = ({
  className,
  currentPage,
  totalPages,
  onPageClick,
  onNextPageClick,
  onPrevPageClick,
}) => {
  const maxVisiblePages = 5;

  const getVisiblePages = () => {
    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | '...')[] = [];
    const leftBoundary = Math.max(2, currentPage - 1);
    const rightBoundary = Math.min(totalPages - 1, currentPage + 1);

    pages.push(1);
    if (leftBoundary > 2) pages.push('...');
    for (let i = leftBoundary; i <= rightBoundary; i++) pages.push(i);
    if (rightBoundary < totalPages - 1) pages.push('...');
    pages.push(totalPages);

    return pages;
  };

  return (
    <nav className={`mt-10 flex max-w-full items-center justify-center gap-5 overflow-x-auto px-4 ${className} `}>
      <button
        className="flex items-center gap-2 text-xs font-bold text-gray-600 disabled:opacity-50"
        onClick={onPrevPageClick}
        disabled={currentPage === 1}
      >
        <ArrowIcon direction="left" />
        <span>Previous</span>
      </button>

      <ul className="flex gap-3">
        {getVisiblePages().map((page, index) => (
          <li key={index} className="min-w-20 text-14 font-medium sm:min-w-36">
            {typeof page === 'number' ? (
              <button
                className={`w-full px-2 py-1 text-xs font-light text-gray-600 ${
                  page === currentPage
                    ? 'relative after:absolute after:-bottom-2 after:left-0 after:h-3 after:w-full after:rounded-full after:bg-orange-600 after:content-[""]'
                    : ''
                }`}
                onClick={() => onPageClick(page)}
              >
                {page}
              </button>
            ) : (
              <span className="px-2 py-1 text-gray-400">...</span>
            )}
          </li>
        ))}
      </ul>
      <button
        className="flex items-center gap-2 text-xs font-bold text-gray-600 disabled:opacity-50"
        onClick={onNextPageClick}
        disabled={currentPage === totalPages}
      >
        <span>Next</span>
        <ArrowIcon direction="right" />
      </button>
    </nav>
  );
};

export default Pagination;
